import React, { useEffect, useState } from 'react';

import { Box, Button, Dialog, Grid, GridSize, IconButton, InputAdornment } from '@material-ui/core';
import { DateRange as DateRangeIcon } from '@material-ui/icons';
import { format, isValid, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { NumberFormatValues } from 'react-number-format';

import 'styled-components/macro';

import { DateRangePicker } from './dateRangePicker';
import { DateRangeType } from './types';

import { MaskedInput } from 'components';
import { TypographyCN } from 'components/TypographyCN';
import { theme } from 'styles';
import { useMobile } from 'utils/useMobile';

type Props = {
  onChange?: (arg: DateRangeType | undefined) => void;
  label?: string;
  labelProps?: any;
  secondLabel?: string;
  placeholderDashboard?: string;
  secondPlaceholderDashboard?: string;
  error?: string;
  grid?: {
    left?: React.ComponentProps<typeof Grid>;
    right?: React.ComponentProps<typeof Grid>;
  };
  value?: DateRangeType | null;
  disabled?: boolean;
  defaultInitialDate?: string;
  defaultEndDate?: string;
  md: GridSize;
};

export function DateRangeKeyboard({
  onChange,
  value,
  label,
  labelProps = {},
  secondLabel,
  error,
  grid,
  disabled,
  defaultInitialDate,
  defaultEndDate,
  placeholderDashboard,
  secondPlaceholderDashboard,
  md,
}: Props) {
  const [isDialogOpen, dispatch] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeType>();
  const [initialDate, setInitialDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [defaultValueInitialDate, setDefaultValueInitialDate] = useState('');
  const [defaultValueEndDate, setDefaultValueEndDate] = useState('');

  const isMobile = useMobile();

  const isDefaultBeginDate = 'Início';
  const isDefaultEndDate = 'Fim';

  useEffect(() => {
    if (defaultInitialDate !== undefined && defaultEndDate !== undefined) {
      setDefaultValueInitialDate(defaultInitialDate);
      setDefaultValueEndDate(defaultEndDate);
    }
  }, [defaultInitialDate, defaultEndDate]);

  useEffect(() => {
    onChange && onChange(dateRange);
  }, [dateRange]);

  const dateMask = '##/##/####';
  const dateFormat = 'dd/MM/yyyy';

  useEffect(() => {
    if (value?.startDate === null && value?.endDate === null) {
      setDateRange(undefined);
      setInitialDate('');
      setEndDate('');
    }
  }, [value]);

  useEffect(() => {
    if (isValid(value?.startDate) && isValid(value?.endDate) && value?.startDate !== null && value?.endDate !== null) {
      setInitialDate(String(format(value?.startDate as Date, dateFormat)));
      setEndDate(String(format(value?.endDate as Date, dateFormat)));
    }
  }, [value]);

  return (
    <Grid
      item
      xs={12}
      md={md}
      style={{
        paddingRight: 0,
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="space-between"
        style={{
          gap: '0.25rem',
        }}
      >
        <TypographyCN
          component="label"
          variant={isMobile ? 'body1' : 'h6'}
          align="center"
          weight="bold"
          fontSize="0.875rem"
          style={{
            textTransform: 'uppercase',
            color: theme.palette.primary.main,
          }}
          {...labelProps}
        >
          {label ?? 'Filtrar por Data'}
        </TypographyCN>
        <Grid container direction="row" style={{ gap: isMobile ? 16 : 0 }} xs={12}>
          <Grid
            item
            xs={12}
            md={4}
            style={{
              paddingRight: isMobile ? 0 : 8,
            }}
            {...grid?.left}
          >
            <MaskedInput
              labelProps={{
                variant: 'body1',
                setColor: theme.palette.primary.main,
                noWrap: true,
              }}
              fullWidth
              disabled={disabled}
              placeholder={placeholderDashboard ? placeholderDashboard : isDefaultBeginDate}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    disabled={disabled}
                    aria-label="open date dialog"
                    onClick={() => dispatch(true)}
                    onMouseDown={e => e.preventDefault()}
                  >
                    <DateRangeIcon
                      style={{
                        color: '#A0A0A0',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              }
              errorMessage={
                (initialDate.replace('_', '').length === dateMask.length &&
                  isValid(dateRange?.startDate) === false &&
                  'Data Invalida') ||
                error
              }
              format={dateMask}
              style={{ padding: 0 }}
              mask="_"
              value={defaultValueInitialDate ? defaultValueInitialDate : initialDate}
              onValueChange={(values: NumberFormatValues) => {
                if (defaultValueInitialDate) {
                  setDefaultValueInitialDate(values.formattedValue);
                  setDateRange(oldRange => ({
                    ...oldRange,
                    startDate: parse(values.formattedValue ?? '', dateFormat, new Date()),
                  }));
                }
                setInitialDate(values.formattedValue);
                setDateRange(oldRange => ({
                  ...oldRange,
                  startDate: parse(values.formattedValue ?? '', dateFormat, new Date()),
                }));
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            style={{
              paddingRight: 0,
              paddingLeft: isMobile ? 0 : 8,
            }}
            {...grid?.right}
          >
            {/* <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="flex-end" minHeight="85px"> */}
            <MaskedInput
              fullWidth
              labelProps={{
                variant: 'body1',
                setColor: theme.palette.primary.main,
                noWrap: true,
              }}
              style={{ padding: 0, marginBottom: '0.25rem' }}
              disabled={disabled}
              placeholder={secondPlaceholderDashboard ? secondPlaceholderDashboard : isDefaultEndDate}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    disabled={disabled}
                    aria-label="open date dialog"
                    onClick={() => dispatch(true)}
                    onMouseDown={e => e.preventDefault()}
                  >
                    <DateRangeIcon
                      style={{
                        color: '#A0A0A0',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              }
              errorMessage={
                endDate.replace('_', '').length === dateMask.length &&
                isValid(dateRange?.endDate) === false &&
                'Data Invalida'
              }
              format={dateMask}
              mask="_"
              value={defaultValueEndDate ? defaultValueEndDate : endDate}
              onValueChange={(values: NumberFormatValues) => {
                if (defaultValueEndDate) {
                  setDefaultValueEndDate(values.formattedValue);
                  setDateRange(oldRange => ({
                    ...oldRange,
                    endDate: parse(values?.formattedValue ?? '', dateFormat, new Date()),
                  }));
                }
                setEndDate(values.formattedValue);
                setDateRange(oldRange => ({
                  ...oldRange,
                  endDate: parse(values?.formattedValue ?? '', dateFormat, new Date()),
                }));
              }}
            />
            {/* </Box> */}
          </Grid>
          <Dialog
            open={isDialogOpen}
            onClose={() => dispatch(false)}
            aria-labelledby="date-dialog-title"
            css={`
              .MuiDialog-paperWidthSm {
                max-width: none;
              }
            `}
          >
            <DateRangePicker
              initialDateRange={{
                startDate: isValid(dateRange?.startDate) ? dateRange?.startDate : undefined,
                endDate: isValid(dateRange?.endDate) ? dateRange?.endDate : undefined,
              }}
              onChange={range => {
                setDateRange(range);
                if (defaultValueInitialDate && defaultValueEndDate) {
                  range.startDate && setDefaultValueInitialDate(format(range.startDate, dateFormat));
                  range.endDate && setDefaultValueEndDate(format(range.endDate, dateFormat));
                }
                range.startDate && setInitialDate(format(range.startDate, dateFormat));
                range.endDate && setEndDate(format(range.endDate, dateFormat));
              }}
              translation={{
                endDate: 'Data Final',
                startDate: 'Data Inicial',
                months: ['JAN', 'FER', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'],
                weekDays: ['DOM', 'SEG', 'TER', 'QUAR', 'QUIN', 'SEX', 'SAB'],
                locale: ptBR,
              }}
              slot={
                <Box display="flex" justifyContent="flex-end">
                  <Button onClick={() => dispatch(false)} color="primary">
                    OK
                  </Button>
                </Box>
              }
            />
          </Dialog>
        </Grid>
      </Box>
    </Grid>
  );
}
